import React, { useState, useEffect } from 'react';
import './Admin.css';

const ServicesManager = () => {
    const [services, setServices] = useState([]);
    const [settings, setSettings] = useState({
        title: 'Услуги',
        showTitle: true,
        columns: 3,
        gap: 30
    });
    const [loading, setLoading] = useState(true);
    const [draggedItem, setDraggedItem] = useState(null);
    const [message, setMessage] = useState('');
    const [uploading, setUploading] = useState({});

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [servicesRes, settingsRes] = await Promise.all([
                fetch('/api/content/services?field=items'),
                fetch('/api/content/services?field=settings')
            ]);

            const servicesData = await servicesRes.json();
            const settingsData = await settingsRes.json();

            let allServices = servicesData.data || [];
            allServices.sort((a, b) => (a.order || 0) - (b.order || 0));

            setServices(allServices);
            setSettings(settingsData.data || { title: 'Услуги', showTitle: true, columns: 3, gap: 30 });
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            setMessage('❌ Ошибка загрузки');
        } finally {
            setLoading(false);
        }
    };

    const saveServices = async (servicesToSave) => {
        try {
            await fetch('/api/content/services', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field: 'items', data: servicesToSave })
            });
        } catch (error) {
            console.error('Ошибка сохранения:', error);
        }
    };

    const saveSettings = async (newSettings) => {
        try {
            await fetch('/api/content/services', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ field: 'settings', data: newSettings })
            });
        } catch (error) {
            console.error('Ошибка сохранения:', error);
        }
    };

    const showMessage = (text) => {
        setMessage(text);
        setTimeout(() => setMessage(''), 2000);
    };

    const addService = () => {
        const newService = {
            id: Date.now().toString(),
            name: 'Новая услуга',
            price: 1000,
            duration: '30 мин',
            description: 'Описание услуги',
            imageUrl: null,
            visible: true,
            order: services.length
        };
        const newServices = [...services, newService];
        setServices(newServices);
        saveServices(newServices);
        showMessage('✅ Услуга добавлена');
    };

    const updateService = (id, field, value) => {
        const updatedServices = services.map(s =>
            s.id === id ? { ...s, [field]: value } : s
        );
        setServices(updatedServices);
        saveServices(updatedServices);
        showMessage('✅ Сохранено');
    };

    const deleteService = (id) => {
        if (!confirm('Удалить услугу?')) return;
        const filteredServices = services.filter(s => s.id !== id);
        const reorderedServices = filteredServices.map((s, idx) => ({ ...s, order: idx }));
        setServices(reorderedServices);
        saveServices(reorderedServices);
        showMessage('✅ Услуга удалена');
    };

    const toggleVisibility = (id) => {
        const updatedServices = services.map(s =>
            s.id === id ? { ...s, visible: !s.visible } : s
        );
        setServices(updatedServices);
        saveServices(updatedServices);
        showMessage('✅ Статус изменен');
    };

    const updateSetting = (key, value) => {
        const newSettings = { ...settings, [key]: value };
        setSettings(newSettings);
        saveSettings(newSettings);
        showMessage('✅ Настройки сохранены');
    };

    const handleImageUpload = async (serviceId, file) => {
        if (!file) return;

        setUploading(prev => ({ ...prev, [serviceId]: true }));
        const formData = new FormData();
        formData.append('serviceImage', file);

        try {
            const response = await fetch('/api/upload/service-image', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                updateService(serviceId, 'imageUrl', data.url);
                showMessage('✅ Изображение загружено');
            }
        } catch (error) {
            console.error('Ошибка загрузки:', error);
            showMessage('❌ Ошибка загрузки изображения');
        } finally {
            setUploading(prev => ({ ...prev, [serviceId]: false }));
        }
    };

    const handleDragStart = (e, index) => {
        setDraggedItem(index);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, dropIndex) => {
        e.preventDefault();
        if (draggedItem === null) return;

        const newServices = [...services];
        const [draggedService] = newServices.splice(draggedItem, 1);
        newServices.splice(dropIndex, 0, draggedService);

        const reorderedServices = newServices.map((s, idx) => ({ ...s, order: idx }));
        setServices(reorderedServices);
        await saveServices(reorderedServices);
        setDraggedItem(null);
        showMessage('✅ Порядок изменен');
    };

    if (loading) return <div className="loading-spinner">Загрузка услуг...</div>;

    const visibleServices = services.filter(s => s.visible);

    return (
        <div className="services-manager">
            <h2>💇 Управление услугами</h2>

            {message && <div className="message success">{message}</div>}

            {/* Настройки */}
            <div className="settings-section">
                <h3>⚙️ Настройки блока</h3>
                <div className="settings-grid">
                    <div className="setting-item">
                        <label>Заголовок</label>
                        <input
                            type="text"
                            value={settings.title}
                            onChange={(e) => updateSetting('title', e.target.value)}
                        />
                    </div>
                    <div className="setting-item checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={settings.showTitle}
                                onChange={(e) => updateSetting('showTitle', e.target.checked)}
                            />
                            Показывать заголовок
                        </label>
                    </div>
                    <div className="setting-item">
                        <label>Колонки (2-4)</label>
                        <select
                            value={settings.columns}
                            onChange={(e) => updateSetting('columns', Number(e.target.value))}
                        >
                            <option value={2}>2 колонки</option>
                            <option value={3}>3 колонки</option>
                            <option value={4}>4 колонки</option>
                        </select>
                    </div>
                    <div className="setting-item">
                        <label>Расстояние между карточками (px)</label>
                        <input
                            type="number"
                            value={settings.gap}
                            onChange={(e) => updateSetting('gap', Number(e.target.value))}
                            min="10"
                            max="50"
                        />
                    </div>
                </div>
            </div>

            {/* Список услуг */}
            <div className="services-list">
                <div className="list-header">
                    <h3>Список услуг (перетаскивайте для сортировки)</h3>
                    <button onClick={addService} className="add-service-btn">+ Добавить услугу</button>
                </div>

                <div className="services-grid">
                    {services.map((service, idx) => (
                        <div
                            key={service.id}
                            className={`service-item ${!service.visible ? 'hidden' : ''} ${draggedItem === idx ? 'dragging' : ''}`}
                            draggable
                            onDragStart={(e) => handleDragStart(e, idx)}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, idx)}
                        >
                            <div className="drag-handle">⋮⋮</div>

                            <div className="service-preview">
                                {service.imageUrl ? (
                                    <img
                                        src={`${service.imageUrl}`}
                                        alt={service.name}
                                        className="service-image"
                                    />
                                ) : (
                                    <div className="service-placeholder">📷</div>
                                )}
                            </div>

                            <div className="service-info">
                                <div className="service-row">
                                    <input
                                        type="text"
                                        value={service.name}
                                        onChange={(e) => updateService(service.id, 'name', e.target.value)}
                                        placeholder="Название"
                                        className="service-name-input"
                                    />
                                </div>
                                <div className="service-row">
                                    <input
                                        type="number"
                                        value={service.price}
                                        onChange={(e) => updateService(service.id, 'price', Number(e.target.value))}
                                        placeholder="Цена"
                                        className="service-price-input"
                                    />
                                    <input
                                        type="text"
                                        value={service.duration}
                                        onChange={(e) => updateService(service.id, 'duration', e.target.value)}
                                        placeholder="Время"
                                        className="service-duration-input"
                                    />
                                </div>
                                <div className="service-row">
                  <textarea
                      value={service.description}
                      onChange={(e) => updateService(service.id, 'description', e.target.value)}
                      placeholder="Описание"
                      className="service-desc-input"
                      rows="2"
                  />
                                </div>
                                <div className="service-row">
                                    <label className="upload-image-label">
                                        {uploading[service.id] ? '⏳ Загрузка...' : '📸 Загрузить фото'}
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={(e) => handleImageUpload(service.id, e.target.files[0])}
                                            style={{ display: 'none' }}
                                        />
                                    </label>
                                    {service.imageUrl && (
                                        <button
                                            onClick={() => updateService(service.id, 'imageUrl', null)}
                                            className="remove-image-btn"
                                        >
                                            ✕ Удалить фото
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="service-actions">
                                <button
                                    onClick={() => toggleVisibility(service.id)}
                                    className="visibility-btn"
                                    title={service.visible ? 'Скрыть' : 'Показать'}
                                >
                                    {service.visible ? '👁️' : '👁️‍🗨️'}
                                </button>
                                <button
                                    onClick={() => deleteService(service.id)}
                                    className="delete-btn"
                                    title="Удалить"
                                >
                                    🗑️
                                </button>
                            </div>

                            <div className="service-order">{idx + 1}</div>
                        </div>
                    ))}
                </div>

                {services.length === 0 && (
                    <div className="empty-state">Нет услуг. Нажмите "+ Добавить услугу"</div>
                )}
            </div>

            {/* Предпросмотр */}
            <div className="preview-section">
                <h3>🖼️ Предпросмотр на сайте</h3>
                <div className="site-preview services-preview">
                    {settings.showTitle && <h4>{settings.title}</h4>}
                    <div
                        className="preview-services-grid"
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${Math.min(settings.columns, 3)}, 1fr)`,
                            gap: `${settings.gap}px`
                        }}
                    >
                        {visibleServices.slice(0, 6).map(service => (
                            <div key={service.id} className="preview-service-card">
                                <div className="preview-service-name">{service.name}</div>
                                <div className="preview-service-price">{service.price} ₽</div>
                                <button className="preview-book-btn">Записаться</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ServicesManager;